import { SectionPaper } from "@/components/Paper";
import { SectionTitle } from "@/components/Title";
import {
  Box,
  Divider,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Select,
  MenuItem,
} from "@mui/material";
import React, { useEffect } from "react";
import { Label } from "@/components/Label";
import {
  UserConfigGetAPI,
  UserConfigUpdateAPI,
} from "@/features/accountSettings/api/UserConfigAPI";
import { ButtonVariantOption, SubmitButton } from "@/components/Button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import { SizingWrapperStyle } from "@/components/Wrapper";
import {
  type UserConfigFormData,
  UserConfigFormDataSchema,
} from "@/features/accountSettings/types/schemas";
import { GetUserMapConfigAPI } from "@/features/map/api/getUserMapConfigAPI";
import {
  INITIAL_MAP_LOCATIONS,
  DEFAULT_MAP_LOCATION,
  findLocationByCoords,
  type MapLocation,
} from "@/utils/mapLocations";

export const UserConfig: React.FC = (_props) => {
  const userConfig = UserConfigGetAPI();
  const { data: mapConfig } = GetUserMapConfigAPI();
  const { trigger } = UserConfigUpdateAPI();

  // 型を用いてReact-Hook-Formのインスタンスを作る
  const reactHookForm = useForm<UserConfigFormData>({
    mode: "all",
    defaultValues: {
      pictureRequestPastDays: 0,
      mapInitLat: DEFAULT_MAP_LOCATION.lat,
      mapInitLon: DEFAULT_MAP_LOCATION.lon,
    },
    resolver: zodResolver(UserConfigFormDataSchema),
  });

  // 現在のフォームの座標の値から駅名を取得
  const convertCoordinatesToStationName = (): string => {
    const lat = reactHookForm.watch("mapInitLat");
    const lon = reactHookForm.watch("mapInitLon");
    const station = findLocationByCoords(lat, lon);

    if (station) {
      return station.name;
    }
    // 一致する駅がない場合は東京駅
    return DEFAULT_MAP_LOCATION.name;
  };

  useEffect(() => {
    // テキストボックスに何か入力してしばらく放置すると、再びサーバーからデータを取りに行った時に入力中の値が上書きされてしまう
    // そこで、一度でも入力があったら更新しないようにする
    if (reactHookForm.formState.isDirty) {
      return;
    }

    if (userConfig) {
      reactHookForm.setValue(
        "pictureRequestPastDays",
        userConfig.pictureRequestPastDays ?? 0
      );
    }

    if (mapConfig) {
      // 現在の座標に一致する駅を検索
      const station = findLocationByCoords(
        mapConfig.mapInitLat,
        mapConfig.mapInitLon
      );
      if (station) {
        reactHookForm.setValue("mapInitLat", station.lat);
        reactHookForm.setValue("mapInitLon", station.lon);
      } else {
        // 一致する駅がない場合はデフォルト（東京駅）を設定
        reactHookForm.setValue("mapInitLat", DEFAULT_MAP_LOCATION.lat);
        reactHookForm.setValue("mapInitLon", DEFAULT_MAP_LOCATION.lon);
      }
    }
  }, [userConfig, mapConfig]);

  const onSubmit = reactHookForm.handleSubmit(
    async (data: UserConfigFormData) => {
      // バリデーションに成功したらサーバーへ送信する
      const result = await trigger(data);
      if (result) {
        toast.success("設定を変更しました");
        reactHookForm.setValue(
          "pictureRequestPastDays",
          result.pictureRequestPastDays
        );
        reactHookForm.setValue("mapInitLat", result.mapInitLat);
        reactHookForm.setValue("mapInitLon", result.mapInitLon);
      }
    }
  );

  return (
    <SectionPaper>
      <Box sx={{ display: "flex" }}>
        <SectionTitle>各種設定</SectionTitle>
      </Box>
      <Divider />
      <Box>
        <Box sx={{ m: 2 }}>
          <Label>不動産登記：経過日数</Label>
          <Box sx={{ mb: 2 }}>
            <FormControl
              variant="outlined"
              error={Boolean(
                reactHookForm.formState.errors.pictureRequestPastDays
              )}
            >
              <InputLabel htmlFor="outlined-adornment-pastdays">
                経過日数
              </InputLabel>
              <OutlinedInput
                sx={{ width: 200 }}
                id="outlined-adornment-pastdays"
                type={"text"}
                {...reactHookForm.register("pictureRequestPastDays", {
                  valueAsNumber: true,
                })}
                error={Boolean(
                  reactHookForm.formState.errors.pictureRequestPastDays
                )}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                endAdornment={
                  <InputAdornment position="end">日</InputAdornment>
                }
                label="経過日数"
              />
              <FormHelperText error>
                {reactHookForm.formState.errors.pictureRequestPastDays?.message}
              </FormHelperText>
            </FormControl>
          </Box>
        </Box>
        <Box sx={{ m: 2 }}>
          <Label>地図初期表示</Label>
          <Box sx={{ mb: 2 }}>
            <FormControl
              variant="outlined"
              error={Boolean(reactHookForm.formState.errors.mapInitLat)}
            >
              <InputLabel id="initial-map-location-select-label">
                地図初期表示
              </InputLabel>
              <Select
                sx={{ width: 200 }}
                labelId="initial-map-location-select-label"
                id="initial-map-location-select"
                value={convertCoordinatesToStationName()}
                label="地図初期表示"
                onChange={(e) => {
                  const selectedStation = INITIAL_MAP_LOCATIONS.find(
                    (location) => location.name === e.target.value
                  );
                  if (selectedStation) {
                    reactHookForm.setValue("mapInitLat", selectedStation.lat, {
                      shouldValidate: true,
                    });
                    reactHookForm.setValue("mapInitLon", selectedStation.lon, {
                      shouldValidate: true,
                    });
                  }
                }}
              >
                {INITIAL_MAP_LOCATIONS.map((location: MapLocation) => (
                  <MenuItem key={location.name} value={location.name}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>
      <Divider />
      <Box
        sx={{
          mr: 2,
          my: 3,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <SubmitButton
          onClick={(_event, cancelLoading) => {
            onSubmit()
              .then(() => {
                cancelLoading();
              })
              .catch((reason) => {
                cancelLoading();
                console.error(reason);
              });
          }}
          timeout={30 * 1000}
          label={"保存"}
          variant={ButtonVariantOption.Contained}
          size={SizingWrapperStyle.SMALL}
          loadingIndicator={"設定中..."}
        />
      </Box>
    </SectionPaper>
  );
};
